import React, { useState, useEffect } from 'react';
import {
  Phone,
  Mail,
  Calendar,
  Search,
  Filter,
  Download,
  RefreshCw,
  LogOut,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedLead, setExpandedLead] = useState(null);
  const [stats, setStats] = useState({ total: 0, new: 0, today: 0, thisWeek: 0 });

  // Fetch leads
  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads?limit=1000`);
      const data = await response.json();
      setLeads(data);
      setFilteredLeads(data);
      calculateStats(data);
      toast.success('Anfragen aktualisiert');
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (data) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const newCount = data.filter(lead => lead.status === 'new').length;
    const todayCount = data.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate >= today;
    }).length;
    const weekCount = data.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate >= weekAgo;
    }).length;

    setStats({
      total: data.length,
      new: newCount,
      today: todayCount,
      thisWeek: weekCount
    });
  };

  // Filter leads
  useEffect(() => {
    let filtered = [...leads];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm) ||
        (lead.message && lead.message.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Service filter
    if (filterService !== 'all') {
      filtered = filtered.filter(lead => lead.service === filterService);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => lead.status === filterStatus);
    }

    setFilteredLeads(filtered);
  }, [searchTerm, filterService, filterStatus, leads]);

  // Initial load
  useEffect(() => {
    fetchLeads();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchLeads, 120000);
    return () => clearInterval(interval);
  }, []);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const getStatusBadge = (status) => {
    const badges = {
      new: <Badge className="bg-red-100 text-red-700 hover:bg-red-100">🔴 Neu</Badge>,
      contacted: <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">🟡 Kontaktiert</Badge>,
      completed: <Badge className="bg-green-100 text-green-700 hover:bg-green-100">🟢 Erledigt</Badge>
    };
    return badges[status] || badges.new;
  };

  // Export to CSV
  const exportToCSV = () => {
    const csvContent = [
      ['Datum', 'Name', 'Telefon', 'E-Mail', 'Service', 'Nachricht', 'Status'],
      ...filteredLeads.map(lead => [
        new Date(lead.created_at).toLocaleString('de-DE'),
        lead.name,
        lead.phone,
        lead.email || '',
        lead.service,
        lead.message || '',
        lead.status
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `anfragen_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV-Datei heruntergeladen');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_login_time');
    toast.success('Erfolgreich abgemeldet');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-blue-100 text-sm mt-1">M.A. Zulassungsdienst - Kundenanfragen</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Gesamt</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Neue Anfragen</p>
                  <p className="text-3xl font-bold text-red-600">{stats.new}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Heute</p>
                  <p className="text-3xl font-bold text-green-600">{stats.today}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Diese Woche</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.thisWeek}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Name, Telefon oder Nachricht suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Service Filter */}
              <Select value={filterService} onValueChange={setFilterService}>
                <SelectTrigger className="w-full lg:w-64 h-12">
                  <SelectValue placeholder="Alle Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Services</SelectItem>
                  <SelectItem value="Schnellzulassung vor Ort (20 Min.)">Schnellzulassung</SelectItem>
                  <SelectItem value="Anmeldung">Anmeldung</SelectItem>
                  <SelectItem value="Ummeldung">Ummeldung</SelectItem>
                  <SelectItem value="Abmeldung">Abmeldung</SelectItem>
                  <SelectItem value="Online-Abmeldung vor Ort">Online-Abmeldung</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full lg:w-48 h-12">
                  <SelectValue placeholder="Alle Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="new">🔴 Neu</SelectItem>
                  <SelectItem value="contacted">🟡 Kontaktiert</SelectItem>
                  <SelectItem value="completed">🟢 Erledigt</SelectItem>
                </SelectContent>
              </Select>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={fetchLeads}
                  variant="outline"
                  className="h-12"
                  disabled={isLoading}
                >
                  <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  onClick={exportToCSV}
                  variant="outline"
                  className="h-12"
                >
                  <Download className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <span>Anfragen ({filteredLeads.length})</span>
              {searchTerm && (
                <Button
                  onClick={() => setSearchTerm('')}
                  variant="ghost"
                  size="sm"
                >
                  Filter zurücksetzen
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Lade Anfragen...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine Anfragen gefunden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="border-2 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    {/* Lead Header */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{lead.name}</h3>
                          <p className="text-sm text-gray-500">{formatDate(lead.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(lead.status)}
                        <Button
                          onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                          variant="ghost"
                          size="sm"
                        >
                          {expandedLead === lead.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </Button>
                      </div>
                    </div>

                    {/* Lead Quick Info */}
                    <div className="grid sm:grid-cols-2 gap-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <a
                          href={`tel:${lead.phone}`}
                          className="text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          {lead.phone}
                        </a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">{lead.service}</Badge>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedLead === lead.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                        {lead.email && (
                          <div className="flex items-start space-x-2">
                            <Mail className="w-4 h-4 text-gray-400 mt-1" />
                            <a
                              href={`mailto:${lead.email}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              {lead.email}
                            </a>
                          </div>
                        )}

                        {lead.message && (
                          <div className="flex items-start space-x-2">
                            <MessageSquare className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            <div className="bg-gray-50 p-4 rounded-lg flex-1">
                              <p className="text-sm font-semibold text-gray-700 mb-2">Nachricht:</p>
                              <p className="text-gray-700 whitespace-pre-wrap">{lead.message}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button
                            onClick={() => window.open(`tel:${lead.phone}`)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Anrufen
                          </Button>
                          <Button
                            onClick={() => window.open(`https://wa.me/49${lead.phone.replace(/^0/, '').replace(/\s/g, '')}`,'_blank')}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
